import { useState, useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import axios from 'axios';
import { Layout } from '@/components/Layout';
import { Button } from '@/components/ui/button';
import { Trophy, Check, Share2, ArrowRight } from 'lucide-react';
import { toast } from 'sonner';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const ROUND_NAMES = { 2: 'Round of 64', 3: 'Round of 32', 4: 'Sweet 16', 5: 'Elite 8', 6: 'Final Four', 7: 'Championship' };
const ROUND_POINTS = { 2: 1, 3: 2, 4: 4, 5: 8, 6: 16, 7: 32 };

export default function SharedBracket() {
  const { bracketId } = useParams();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [activeRegion, setActiveRegion] = useState('East');

  useEffect(() => {
    axios.get(`${API}/bracket/shared/${bracketId}`)
      .then(res => setData(res.data))
      .catch(() => setError('Bracket not found'))
      .finally(() => setLoading(false));
  }, [bracketId]);

  useEffect(() => {
    if (data?.bracket?.username) {
      document.title = data.bracket.username + " Bracket | PrimeStatLab";
    }
  }, [data]);

  const copyLink = () => {
    const ogUrl = `${process.env.REACT_APP_BACKEND_URL}/api/bracket/og/${bracketId}`;
    navigator.clipboard.writeText(ogUrl);
    toast.success('Link copied!');
  };

  if (loading) return (
    <Layout>
      <div className="min-h-screen flex items-center justify-center">
        <div className="animate-pulse text-primary font-mono">Loading bracket...</div>
      </div>
    </Layout>
  );

  if (error || !data) return (
    <Layout>
      <div className="min-h-screen flex flex-col items-center justify-center px-4">
        <h1 className="text-2xl font-heading font-bold mb-4">Bracket Not Found</h1>
        <p className="text-muted-foreground mb-6">This bracket may have been removed or the link is invalid.</p>
        <Link to="/bracket"><Button>Go to Bracket Challenge</Button></Link>
      </div>
    </Layout>
  );

  const { bracket, tournament } = data;
  const picksMap = {};
  (bracket.picks || []).forEach(p => { picksMap[p.game_id] = { seed: p.picked_seed, team: p.picked_team }; });
  const regions = ['East', 'West', 'South', 'Midwest'];

  return (
    <Layout>
      <div className="container mx-auto px-4 py-8 max-w-7xl">
        {/* Header */}
        <div className="text-center mb-8" data-testid="shared-bracket-header">
          <Trophy className="mx-auto text-accent mb-3" size={48} />
          <h1 className="text-3xl sm:text-4xl lg:text-5xl font-heading font-bold mb-2">
            {bracket.username}'s Bracket
          </h1>
          <p className="text-muted-foreground text-sm mb-4">{tournament.name}</p>
          
          <div className="flex justify-center gap-6 mb-6">
            <div className="text-center">
              <div className="font-mono text-2xl font-bold text-primary">{bracket.points}</div>
              <div className="text-xs text-muted-foreground uppercase">Points</div>
            </div>
            <div className="text-center">
              <div className="font-mono text-2xl font-bold">{bracket.correct_picks}</div>
              <div className="text-xs text-muted-foreground uppercase">Correct</div>
            </div>
          </div>

          <div className="flex justify-center gap-3">
            <Button variant="outline" onClick={copyLink} className="font-mono text-xs uppercase gap-2" data-testid="copy-shared-link">
              <Share2 size={14} /> Copy Link
            </Button>
            <Link to="/bracket">
              <Button className="bg-primary hover:bg-primary/90 font-mono text-xs uppercase gap-2" data-testid="fill-own-bracket-cta">
                Fill Out YOUR Bracket <ArrowRight size={14} />
              </Button>
            </Link>
          </div>
        </div>

        {/* Region Tabs */}
        <div className="flex flex-wrap justify-center gap-2 mb-6" data-testid="shared-region-tabs">
          {regions.map(region => (
            <button key={region} onClick={() => setActiveRegion(region)}
              className={`px-5 py-2.5 font-mono text-sm uppercase tracking-wider rounded transition-all ${
                activeRegion === region ? 'bg-primary text-primary-foreground' : 'bg-card border border-border hover:border-primary/50'
              }`}>{region}</button>
          ))}
          <button onClick={() => setActiveRegion('Final Four')}
            className={`px-5 py-2.5 font-mono text-sm uppercase tracking-wider rounded transition-all ${
              activeRegion === 'Final Four' ? 'bg-accent text-accent-foreground' : 'bg-card border border-border hover:border-accent/50'
            }`}>Final Four</button>
        </div>

        {/* Bracket View */}
        {activeRegion !== 'Final Four' ? (
          <SharedRegionView tournament={tournament} region={activeRegion} picksMap={picksMap} />
        ) : (
          <SharedFinalFourView tournament={tournament} picksMap={picksMap} />
        )}

        {/* CTA */}
        <div className="text-center mt-12 p-8 bg-gradient-to-b from-primary/5 to-transparent border border-primary/20 rounded-lg max-w-lg mx-auto" data-testid="shared-bracket-cta">
          <h2 className="text-xl font-heading font-bold mb-3">Think you can do better?</h2>
          <p className="text-sm text-muted-foreground mb-5">Fill out your own bracket and compete on the leaderboard!</p>
          <Link to="/bracket">
            <Button className="bg-primary hover:bg-primary/90 font-mono uppercase tracking-wider px-8 py-3">
              Start My Bracket
            </Button>
          </Link>
        </div>
      </div>
    </Layout>
  );
}

/* Read-only region bracket view */
function SharedRegionView({ tournament, region, picksMap }) {
  const regionGames = tournament.games.filter(g => g.region === region);
  const rounds = [2, 3, 4, 5];

  return (
    <div className="overflow-x-auto pb-4">
      <div className="min-w-[800px]">
        <div className="flex gap-3">
          {rounds.map(round => {
            const games = regionGames.filter(g => g.round_num === round);
            const spacing = round === 2 ? 'gap-1' : round === 3 ? 'gap-6' : round === 4 ? 'gap-20' : 'gap-0';
            return (
              <div key={round} className="flex-1">
                <div className="text-center mb-3">
                  <span className="font-mono text-[10px] uppercase tracking-widest text-muted-foreground">{ROUND_NAMES[round]}</span>
                  <span className="font-mono text-[10px] text-primary ml-1">({ROUND_POINTS[round]}pt)</span>
                </div>
                <div className={`flex flex-col justify-around h-full ${spacing}`}>
                  {games.map(game => (
                    <SharedGameCard key={game.game_id} game={game} userPick={picksMap[game.game_id]} />
                  ))}
                </div>
              </div>
            );
          })}
          <div className="flex-shrink-0 flex items-center">
            <div className="text-center">
              <span className="font-mono text-[10px] uppercase tracking-widest text-muted-foreground block mb-2">{region} Champ</span>
              <div className="bg-primary/10 border-2 border-primary/50 rounded-lg px-4 py-3 min-w-[110px]">
                <span className="font-mono text-sm font-bold">
                  {regionGames.find(g => g.round_num === 5)?.winner_name ||
                   picksMap[regionGames.find(g => g.round_num === 5)?.game_id]?.team || '???'}
                </span>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

/* Read-only Final Four view */
function SharedFinalFourView({ tournament, picksMap }) {
  const ffGames = tournament.games.filter(g => g.round_num === 6);
  const champGame = tournament.games.find(g => g.round_num === 7);

  return (
    <div className="max-w-3xl mx-auto">
      <div className="grid grid-cols-3 gap-6 items-center">
        <div className="space-y-4">
          <div className="font-mono text-[10px] uppercase tracking-widest text-center text-muted-foreground">
            Final Four <span className="text-primary">({ROUND_POINTS[6]}pt)</span>
          </div>
          {ffGames.map(game => <SharedGameCard key={game.game_id} game={game} userPick={picksMap[game.game_id]} />)}
        </div>
        <div className="text-center space-y-4">
          <div className="font-mono text-[10px] uppercase tracking-widest text-accent">Championship ({ROUND_POINTS[7]}pt)</div>
          {champGame && <SharedGameCard game={champGame} userPick={picksMap[champGame.game_id]} isChampionship />}
          <div className="pt-2">
            <Trophy className="mx-auto text-accent mb-1" size={28} />
            <div className="font-mono text-[10px] uppercase tracking-widest text-muted-foreground">Champion</div>
            <div className="bg-accent/10 border-2 border-accent/50 rounded-lg p-3 mt-1">
              <span className="font-heading text-lg font-bold">
                {champGame?.winner_name || picksMap[champGame?.game_id]?.team || '???'}
              </span>
            </div>
          </div>
        </div>
        <div />
      </div>
    </div>
  );
}

/* Read-only game card */
function SharedGameCard({ game, userPick, isChampionship }) {
  const hasWinner = game.is_complete;
  const team1Picked = userPick?.seed === game.team1_seed && userPick?.team === game.team1_name;
  const team2Picked = userPick?.seed === game.team2_seed && userPick?.team === game.team2_name;
  const team1Correct = hasWinner && team1Picked && game.winner_name === game.team1_name;
  const team2Correct = hasWinner && team2Picked && game.winner_name === game.team2_name;
  const team1Wrong = hasWinner && team1Picked && game.winner_name !== game.team1_name;
  const team2Wrong = hasWinner && team2Picked && game.winner_name !== game.team2_name;

  return (
    <div className={`rounded-lg overflow-hidden border ${isChampionship ? 'border-accent/50' : 'border-border'}`}>
      <SharedTeamRow team={game.team1_name} seed={game.team1_seed} score={game.team1_score}
        isPicked={team1Picked} isCorrect={team1Correct} isWrong={team1Wrong}
        isActualWinner={hasWinner && game.winner_name === game.team1_name}
        isActualLoser={hasWinner && game.winner_name !== game.team1_name} />
      <div className="border-t border-border/50" />
      <SharedTeamRow team={game.team2_name} seed={game.team2_seed} score={game.team2_score}
        isPicked={team2Picked} isCorrect={team2Correct} isWrong={team2Wrong}
        isActualWinner={hasWinner && game.winner_name === game.team2_name}
        isActualLoser={hasWinner && game.winner_name !== game.team2_name} />
    </div>
  );
}

function SharedTeamRow({ team, seed, score, isPicked, isCorrect, isWrong, isActualWinner, isActualLoser }) {
  return (
    <div className={`px-2.5 py-1.5 flex items-center justify-between ${
      isCorrect ? 'bg-accent/20 text-accent' :
      isWrong ? 'bg-loss/10 text-loss line-through' :
      isActualWinner ? 'bg-accent/10' :
      isActualLoser ? 'opacity-40' :
      isPicked ? 'bg-primary/15 text-primary' : ''
    } ${!team ? 'opacity-25' : ''}`}>
      <div className="flex items-center gap-1.5 min-w-0">
        <span className="font-mono text-[10px] text-muted-foreground w-4 flex-shrink-0 text-right">{seed || '-'}</span>
        <span className="font-mono text-xs truncate">{team || 'TBD'}</span>
      </div>
      <div className="flex items-center gap-1 flex-shrink-0">
        {isPicked && !isCorrect && !isWrong && <Check size={12} className="text-primary" />}
        {isCorrect && <Check size={12} className="text-accent" />}
        {score !== null && score !== undefined && <span className="font-mono text-[10px] ml-1">{score}</span>}
      </div>
    </div>
  );
}
